/**
 * Application providers.
 *
 * Three. Plan D8 permits a router and React Query for server state, and no
 * state library beyond them — component state stays in components, server data
 * stays in the query cache, and there is no Redux or Zustand.
 *
 * `ToastProvider` is not a state library and is not a third store: it holds a
 * list of messages currently on screen and nothing else. It is a provider only
 * because a toast has to outlive the component that raised it — approving a
 * record navigates away from the row that was approved, and a notice rendered
 * inside that row would unmount before anybody read it.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ToastProvider } from './components/common';
import { AppRoutes } from './routes';
import { AuthProvider } from './hooks/useAuth';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Staff keep this open all day across several tabs. Refetching every table
      // on every tab focus is noise, not freshness; screens that mutate data
      // invalidate their own keys.
      refetchOnWindowFocus: false,
      // One retry, because the common failure here is "the backend is not
      // running" and repeating it five times only delays the error message.
      retry: 1,
      staleTime: 30_000,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/*
        Both v7 flags are opted into now, while there are no screens to break.
        Without them React Router logs two deprecation warnings on every page
        load, in the browser console and in the test output - noise that six
        developers would learn to scroll past, taking real warnings with it.
      */}
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* Inside the router, so a toast can be raised during navigation. */}
        {/*
          Inside the router, because signing out redirects; outside the toasts,
          because a session ending is not a toast - it changes the whole screen.
        */}
        <AuthProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
